import { Modal, Form, OverlayTrigger, Popover } from "react-bootstrap";
import { useEffect, useMemo, useState } from "react";
import { Alphabet, At, InfoCircleFill, Key, Send } from "react-bootstrap-icons";
import { useForm } from "react-hook-form";
import { HeaderText } from "./Controls/HeaderText";
import { Input } from "./Controls/Input";
import { Button } from "./Controls/Button";
import { Select } from "./Controls/Select";
import { UNASSIGNED_GROUP_ID } from "./SubscriptionManageGroupParkersPage";
import { DateTimeFormatter, LocalDate } from "@js-joda/core";
import { Api } from "./Data/Api";
import { FacilityInviteCredentialCapability } from "./Data/ApiTypes";

export interface InviteParkerModalProps {
  show: boolean;
  setShow: (show: boolean) => void;
  submit: (data: InviteParkerForm) => void;
  facilityId: number;
  plans?: any[];
  groups?: any[];
  isFromGroupContext?: boolean;
  isEnhancedGroup: boolean;
  /** When set, the group dropdown is pre-selected to this group when the modal opens (group context only). */
  defaultSubscriptionGroupId?: number | null;
  /** Subscription start date. When set, the start date picker minimum (and default) is this date, or today if it's in the past. */
  subscriptionStart?: LocalDate;
}

export interface InviteParkerForm {
  name: string;
  phoneNumber: string;
  emailAddress: string;
  isLoginEnabled: boolean;
  planId: number;
  subscriptionGroupId: number | null;
  startDate: string;
  pendingIntegrationCredential: string | null;
}

export function InviteParkerModal({
  show,
  setShow,
  submit,
  facilityId,
  plans,
  groups,
  isEnhancedGroup,
  isFromGroupContext = false,
  defaultSubscriptionGroupId,
  subscriptionStart,
}: InviteParkerModalProps) {
  const today = LocalDate.now();
  const minStartDate = (!subscriptionStart || subscriptionStart.isBefore(today) ? today : subscriptionStart)
    .format(DateTimeFormatter.ISO_LOCAL_DATE).toString();

  const [credentialCapability, setCredentialCapability] = useState<FacilityInviteCredentialCapability>({
    hasCredentialUpdateIntegration: false,
    rfidValidationPattern: null,
    rfidValidationMaxLength: null,
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm<InviteParkerForm>({
    defaultValues: {
      name: "",
      emailAddress: "",
      phoneNumber: "",
      isLoginEnabled: false,
      subscriptionGroupId: UNASSIGNED_GROUP_ID,
      startDate: minStartDate,
      pendingIntegrationCredential: null,
    },
  });

  useEffect(() => {
    if (!show) return;

    let ignore = false;
    Api.facilityParkerInviteCredentialCapability(facilityId).then((res) => {
      if (!ignore && res.isOk) {
        setCredentialCapability(res.value);
      }
    });

    return () => {
      ignore = true;
    };
  }, [show, facilityId]);

  const showCredentialField = credentialCapability.hasCredentialUpdateIntegration;
  const maxCredentialLength = credentialCapability.rfidValidationMaxLength ?? 20;

  useEffect(() => {
    if (!showCredentialField) {
      setValue("pendingIntegrationCredential", null);
    }
  }, [showCredentialField, setValue]);

  // When modal opens with a default group (from filter), pre-select the group dropdown
  useEffect(() => {
    if (show && groups && defaultSubscriptionGroupId != null && defaultSubscriptionGroupId !== UNASSIGNED_GROUP_ID) {
      setValue("subscriptionGroupId", defaultSubscriptionGroupId);
    } else if (show && groups) {
      setValue("subscriptionGroupId", UNASSIGNED_GROUP_ID);
    }
  }, [show, defaultSubscriptionGroupId, groups, setValue]);

  const isLoginEnabledValue = watch("isLoginEnabled");
  const eligiblePlans = useMemo(
    () => (plans ?? []).filter((plan) => !plan.isUsageBasedBilling),
    [plans]
  );

  function closeModal() {
    setShow(false);
    reset();
  }

  function submitImpl(data: InviteParkerForm) {
    setShow(false);
    submit({
      ...data,
      pendingIntegrationCredential: showCredentialField ? data.pendingIntegrationCredential : null,
    });
    reset();
  }

  return (
    <Modal centered show={show} onEscapeKeyDown={closeModal}>
      <form onSubmit={handleSubmit(submitImpl)}>
        <Modal.Header>
          <HeaderText>Add Parker</HeaderText>
        </Modal.Header>
        <Modal.Body>
          {!isFromGroupContext && (
            <>
              <div
                className="text-secondary gap-4 px-2 mb-4"
                style={{
                  display: "grid",
                  gridTemplateColumns: "auto 1fr",
                  alignItems: "center",
                }}
              >
                <Send size="32" className="text-primary" />{" "}
                <span>Invite someone to your plan to let them park!</span>
              </div>
            </>
          )}
          <div
            className="text-secondary gap-4 px-2 mb-3"
            style={{
              display: "grid",
              gridTemplateColumns: "auto 1fr",
              alignItems: "center",
            }}
          >
            <Key size="32" className="text-primary" />{" "}
            <span>
              You can add or remove spots or access from them at any time
            </span>
          </div>
          <Input
            autoFocus
            type="text"
            label="Name"
            icon={<Alphabet className="fs-5" />}
            placeholder="Enter their Name"
            {...register("name", { required: "Name is required" })}
            error={errors.name}
          />

          <Input
            type="email"
            label={
              isFromGroupContext || !isLoginEnabledValue
                ? "Email Address (Optional)"
                : "Email Address"
            }
            icon={<At className="fs-5" />}
            placeholder="Enter their Email address"
            {...register("emailAddress", {
              validate: (value, formValues) => {
                const trimmed = value?.trim() ?? "";
                if (formValues.isLoginEnabled && !isFromGroupContext && !trimmed) {
                  return "Email Address is required";
                }
                if (
                  trimmed &&
                  !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(trimmed)
                ) {
                  return "Invalid email address";
                }
                return true;
              },
            })}
            error={errors.emailAddress}
          />

          {!isFromGroupContext && (
            <Form.Check
              type="switch"
              id="isLoginEnabled"
              label="Invite parker to manage their account?"
              {...register("isLoginEnabled")}
              className="mb-3"
            />
          )}
          {eligiblePlans.length > 0 && !isFromGroupContext && (
            <Select
              label="Plan"
              {...register("planId", {
                valueAsNumber: true,
                required: "Plan is required",
              })}
            >
              {eligiblePlans.map((plan) => (
                <option key={plan.id} value={plan.id}>
                  {plan.name}
                </option>
              ))}
            </Select>
          )}
          {groups && isFromGroupContext && (
            <Select
              label="Group"
              {...register("subscriptionGroupId", { valueAsNumber: true })}
            >
              <option key={UNASSIGNED_GROUP_ID} value={UNASSIGNED_GROUP_ID}>
                Unassigned
              </option>
              {groups.map((group) => (
                <option key={group.id} value={group.id}>
                  {group.name}
                </option>
              ))}
            </Select>
          )}
          {(isEnhancedGroup) && (
            <Form.Group className="mb-3">
              <Form.Label>Start date</Form.Label>
              <Form.Control
                type="date"
                min={minStartDate}
                {...register("startDate", {
                  required: "Start date is required",
                })}
              />
              {errors.startDate && (
                <div className="text-danger small">
                  {errors.startDate.message}
                </div>
              )}
            </Form.Group>
          )}
          {showCredentialField && (
            <Form.Group className="mb-3">
              <Form.Label className="mb-1">
                RFID Number (Optional){" "}
                <OverlayTrigger
                  placement="top"
                  container={document.body}
                  popperConfig={{
                    strategy: "fixed",
                    modifiers: [
                      {
                        name: "preventOverflow",
                        options: {
                          padding: 8,
                          boundary: "viewport",
                        },
                      },
                      {
                        name: "flip",
                        options: {
                          fallbackPlacements: ["bottom", "left", "right"],
                        },
                      },
                    ],
                  }}
                  overlay={
                    <Popover id="sub-parker-rfid-popover" style={{ pointerEvents: "none", maxWidth: 280 }}>
                      <Popover.Header as="h4">RFID Number</Popover.Header>
                      <Popover.Body>
                        Assign a physical RFID number to override the auto-generated credential when this parker is registered with the facility integration.
                      </Popover.Body>
                    </Popover>
                  }
                >
                  <span className="d-inline-flex align-items-center" style={{ cursor: "help", marginLeft: "4px", width: "16px", height: "16px" }}>
                    <InfoCircleFill size={14} />
                  </span>
                </OverlayTrigger>
              </Form.Label>
              <Form.Control
                type="text"
                maxLength={maxCredentialLength}
                {...register("pendingIntegrationCredential", {
                  maxLength: {
                    value: maxCredentialLength,
                    message: `RFID number must be ${maxCredentialLength} characters or fewer`,
                  },
                  setValueAs: (value: string | null) => {
                    const trimmed = value?.trim();
                    return trimmed ? trimmed : null;
                  },
                })}
              />
              {errors.pendingIntegrationCredential && (
                <Form.Text className="d-block text-danger ms-1">
                  {errors.pendingIntegrationCredential.message}
                </Form.Text>
              )}
            </Form.Group>
          )}
        </Modal.Body>
        <Modal.Footer className="d-flex">
          <Button className="flex-grow-1" onClick={closeModal}>
            Cancel
          </Button>
          <Button className="flex-grow-1 btn-main" type="submit">
            Confirm
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
}
